import { from, Observable } from "rxjs";
import { backOff } from "exponential-backoff";
import axios from "axios";
import { getConfig } from "@/get-config";
import store from "@/store";
import { logout } from "@/features/auth/duck/auth";
import { userReset } from "@/features/user/duck/user";
import { resetAiChatBot } from "@/features/aiAgentChatBot/duck/aiChatBot";

const { appDomainUrl, aiDomainUrl } = getConfig();
const API_DOMAINS = {
  default: appDomainUrl,
  ai: aiDomainUrl
};

export const observableGET = (url, properties, baseDomain = "default") => {
  return from(GET(url, properties, baseDomain));
};

export const observablePOST = (url, properties, baseDomain = "default") => {
  //for ai api request baseDomain is to set as 'ai'
  return from(POST(url, properties, baseDomain));
};

// helpers/observablePostStream.js
export const observableFetchStream = (
  url,
  method = "POST",
  properties = {},
  baseDomain = "default"
) => {
  return from(apiStreamingHandler(url, method, properties, baseDomain));
};

export const observablePUT = (url, properties) => {
  return from(PUT(url, properties));
};

export const observableDELETE = (url, properties) => {
  return from(DELETE(url, properties));
};

export const observablePATCH = (url, properties) => {
  return from(PATCH(url, properties));
};

export const GET = async (url, properties, baseDomain = "default") => {
  return apiHandler(url, "get", properties, baseDomain);
};

export const POST = async (url, properties, baseDomain = "default") => {
  return apiHandler(url, "post", properties, baseDomain);
};

export const PUT = async (url, properties) => {
  return apiHandler(url, "put", properties);
};

export const PATCH = async (url, properties) => {
  return apiHandler(url, "patch", properties);
};

export const DELETE = async (url, properties) => {
  return apiHandler(url, "delete", properties);
};

export const handleObservableError = (error, fallback) => {
  return fallback;
};

export const apiHandler = async (
  url,
  method,
  properties = {},
  baseDomain = "default"
) => {
  const token = localStorage.getItem("token");

  const {
    accept,
    body,
    headers: customHeader,
    responseType = "json",
    params
  } = properties;
  const API_DOMAINS = {
    default: appDomainUrl,
    ai: aiDomainUrl
  };
  const domain = API_DOMAINS[baseDomain] || API_DOMAINS.default;
  const fullUrl = `${domain}${url}`;

  const headers = customHeader || {};

  if (token) {
    headers.Authorization = `Bearer ${token}`; // Assuming Bearer token
  }

  if (accept) {
    headers.Accept = accept;
  }

  return backOff(
    () => {
      return axios({
        url: fullUrl, // Use the constructed full URL
        method,
        responseType,
        headers,
        data: body,
        params
      })
        .then((response) => {
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    {
      maxDelay: 60000,
      jitter: "full",
      numOfAttempts: 2,
      timeMultiple: 2.78316,
      retry: handleRetry
    }
  );
};

export const apiStreamingHandler = (
  url,
  method = "POST",
  properties = {},
  baseDomain = "default"
) => {
  return new Observable((observer) => {
    const controller = new AbortController();
    const signal = controller.signal;

    const token = localStorage.getItem("token");

    const {
      accept = "text/event-stream",
      body,
      headers: customHeaders = {},
      params
    } = properties;

    const domain = API_DOMAINS[baseDomain] || API_DOMAINS.default;

    // Build query string if needed
    const queryString = params
      ? "?" +
        Object.entries(params)
          .map(
            ([key, value]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
          )
          .join("&")
      : "";

    const fullUrl = `${domain}${url}${queryString}`;

    const headers = {
      ...customHeaders,
      Accept: accept,
      "Content-Type": "application/json"
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetch(fullUrl, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal
    })
      .then((response) => {
        if (!response.ok) {
          observer.error(new Error(`HTTP error: ${response.status}`));
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        const read = () => {
          reader?.read().then(({ done, value }) => {
            if (done) {
              observer.complete();
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (let line of lines) {
              line = line.trim();
              if (line.startsWith("data:")) {
                const jsonStr = line.slice(5).trim();
                if (jsonStr) {
                  try {
                    const parsed = JSON.parse(jsonStr);
                    observer.next(parsed);
                  } catch (err) {
                    console.error("JSON parse error in stream:", jsonStr, err);
                  }
                }
              }
            }

            read();
          });
        };

        read();
      })
      .catch((error) => {
        observer.error(error);
      });
  });
};

const handleRetry = (error) => {
  return checkRetryStatus(error) || checkRetryMessage(error);
};

const checkRetryStatus = (error) => {
  // Only retry on error status: 0, 403 and 502
  const token = localStorage.getItem("token");
  const status = error?.response?.status || 500;
  if (status === 401 && token) {
    // TODO:Refresh token when token is expired
    alert("Your session has expired. You will be logged out.");
    store.dispatch(logout({ reload: true }));
    store.dispatch(resetAiChatBot());
    store.dispatch(userReset());
    return status === 401;
  }
  return status === 0 || status === 403 || status === 502 || status === 503;
};

const checkRetryMessage = (error) => {
  const message = error?.message;
  return message && message === "Network Error";
};
